import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import InvoicesTable from '../../Invoices/InvoicesTable';
import PageTitle from '../../../../layouts/PageTitle';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faUser,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faCalendarAlt,
  faEdit,
  faArrowLeft,
  faPlus,
  faFileInvoice,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { Button, Card, Row, Col, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { MapPin, Building, User, X, Plus } from 'lucide-react';
import Select from 'react-select';
import swal from 'sweetalert';

const ViewCustomerDetailed = () => {
  const { companyId, customerId } = useParams();
  const history = useHistory();
  const [customer, setCustomer] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState({
    totalEarned: 0,
    invoiceCount: 0,
    pendingInvoices: 0,
    overdueInvoices: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    address_type: 'billing',
    street1: '',
    street2: '',
    postalcode: '',
    city: '',
    country: 'Sverige'
  });
  const [contactForm, setContactForm] = useState({
    contact_name: '',
    role: '',
    contact_email: '',
    contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch customer details and invoices in parallel
        const [customerResponse, invoicesResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true }),
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}/invoices`), { withCredentials: true })
        ]);

        console.log("Print out invoice response: ", invoicesResponse);

        setCustomer(customerResponse.data);
        setInvoices(invoicesResponse.data.data);
        calculateStatistics(invoicesResponse.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Kunde inte hämta kunddata. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, customerId]);

  const calculateStatistics = (invoices) => {
    console.log("Invoice test:", invoices);
    const totalEarned = invoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending' || inv.status === 'sent').length;

    // Calculate overdue invoices
    const today = new Date();
    const overdueInvoices = invoices.filter(invoice => {
      if (invoice.status === 'paid') return false; // Skip paid invoices

      // Check if invoice has a due date and if it's past today
      if (invoice.due_date) {
        const dueDate = new Date(invoice.due_date);
        return dueDate < today;
      }
      return false;
    }).length;

    setStatistics({
      totalEarned,
      invoiceCount: invoices.length,
      pendingInvoices,
      overdueInvoices
    });
  };

  // Handle address form submission
  const handleAddAddress = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/sales/customers/${customerId}/addresses`),
        addressForm,
        { withCredentials: true }
      );

      // Update customer data with new address
      setCustomer(prev => ({
        ...prev,
        addresses: [...(prev.addresses || []), response.data]
      }));

      // Reset form and close modal
      setAddressForm({
        address_type: 'billing',
        street1: '',
        street2: '',
        postalcode: '',
        city: '',
        country: 'Sverige'
      });
      setShowAddressModal(false);
    } catch (error) {
      console.error('Error adding address:', error);
      swal('Fel', 'Kunde inte lägga till adress. Försök igen.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle contact form submission
  const handleAddContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/sales/customers/${customerId}/contacts`),
        contactForm,
        { withCredentials: true }
      );

      // Update customer data with new contact
      setCustomer(prev => ({
        ...prev,
        contacts: [...(prev.contacts || []), response.data]
      }));

      // Reset form and close modal
      setContactForm({
        contact_name: '',
        role: '',
        contact_email: '',
        contact_phone: ''
      });
      setShowContactModal(false);
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Kunde inte lägga till kontaktperson. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCustomer = () => {
    setEditForm({
      type: customer.type,
      name: customer.name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      orgnumber: customer.orgnumber || '',
      vatnumber: customer.vatnumber || '',
      payment_terms: customer.payment_terms || 30
    });
    setShowEditModal(true);
  };

  // Add this function to handle form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.put(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        editForm,
        { withCredentials: true }
      );

      setCustomer(response.data);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Kunde inte uppdatera kunden. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger" className="text-center">
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => history.goBack()}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Gå tillbaka
          </Button>
        </Alert>
      </div>
    );
  }

  if (!customer) {
    return (
      <Alert variant="warning" className="text-center">
        <h5>Kunden hittades inte</h5>
        <Button variant="outline-warning" onClick={() => history.goBack()}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Gå tillbaka
        </Button>
      </Alert>
    );
  }

  const isCompany = customer.type === 'company';

  return (
    <>
      <style>{`
        .customer-detail-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        
        .customer-header {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .customer-avatar {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: #64748b;
        }

        .customer-name {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        .customer-type {
          background: #f1f5f9;
          color: #64748b;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border: 1px solid #e2e8f0;
        }

        .customer-subtitle {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.25rem;
        }
        
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .info-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .info-card-header {
          display: flex;
          justify-content: between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .info-card-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }
        
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .info-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        
        .info-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1e293b;
        }
        
        .address-card, .contact-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          width: 100%;
          max-width: 350px;
        }
        
        .address-grid, .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .add-item-card {
          background: #f0fdf4;
          border: 2px dashed #16a34a;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          width: 100%;
          max-width: 350px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          min-height: 120px;
        }

        .add-item-card:hover {
          background: #dcfce7;
          border-color: #15803d;
          transform: translateY(-2px);
        }

        .add-item-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #16a34a;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.75rem;
          font-size: 1.125rem;
        }

        .add-item-text {
          color: #16a34a;
          font-weight: 600;
          margin: 0;
        }

        .add-item-subtitle {
          color: #059669;
          font-size: 0.875rem;
          margin: 0;
          margin-top: 0.25rem;
        }
        
        .address-type, .contact-role {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #3b82f6;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 1rem;
        }
        
        .address-details, .contact-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .detail-line {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          color: #374151;
        }
        
        .detail-icon {
          width: 16px;
          text-align: center;
          color: #64748b;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem 2rem;
          color: #64748b;
        }
        
        .empty-state-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.5rem;
          color: #94a3b8;
        }
        
        .action-buttons {
          display: flex;
          gap: 0.75rem;
          margin-top: 1rem;
        }
        
        .btn-modern {
          border-radius: 8px;
          padding: 0.75rem 1.25rem;
          font-weight: 600;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .btn-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        @media (max-width: 768px) {
          .customer-header {
            padding: 1.5rem;
          }
          
          .customer-name {
            font-size: 1.5rem;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .info-grid {
            grid-template-columns: 1fr;
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .address-grid, .contact-grid {
            grid-template-columns: 1fr;
          }
          
          .address-card, .contact-card, .add-item-card {
            max-width: 100%;
          }
        }
      `}</style>

      <PageTitle activeMenu="Kunddetaljer" motherMenu="Kunder" />

      <div className="customer-detail-container">
        {/* Customer Header */}
        <div className="customer-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="d-flex align-items-center gap-3">
                <div className="customer-avatar">
                  <FontAwesomeIcon icon={isCompany ? faBuilding : faUser} />
                </div>
                <div>
                  <h1 className="customer-name">
                    {isCompany ? customer.name : `${customer.first_name} ${customer.last_name}`}
                  </h1>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <Button
                variant="outline-secondary"
                className="btn-modern"
                onClick={() => history.goBack()}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Tillbaka
              </Button>
              <Button
                variant="primary"
                className="btn-modern"
                onClick={() => { handleEditCustomer() }}
              >
                <FontAwesomeIcon icon={faEdit} />
                Redigera
              </Button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
              <FontAwesomeIcon icon={faFileInvoice} />
            </div>
            <div className="stat-value" style={{ color: '#3b82f6' }}>
              {statistics.invoiceCount}
            </div>
            <div className="stat-label">Totalt antal fakturor</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
              <FontAwesomeIcon icon={faFileInvoice} />
            </div>
            <div className="stat-value" style={{ color: '#16a34a' }}>
              {new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(statistics.totalEarned)}
            </div>
            <div className="stat-label">Total intjänad summa</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="stat-value" style={{ color: '#d97706' }}>
              {statistics.pendingInvoices}
            </div>
            <div className="stat-label">Väntande fakturor</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="stat-value" style={{ color: '#dc2626' }}>
              {statistics.overdueInvoices}
            </div>
            <div className="stat-label">Förfallna fakturor</div>
          </div>
        </div>

        <Row>
          <Col lg={8}>
            {/* Basic Information */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">
                  <FontAwesomeIcon icon={isCompany ? faBuilding : faUser} />
                  Grundläggande information
                </h3>
              </div>

              <div className="info-grid">
                {isCompany ? (
                  <>
                    <div className="info-item">
                      <span className="info-label">Företagsnamn</span>
                      <span className="info-value">{customer.name || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Organisationsnummer</span>
                      <span className="info-value">{customer.orgnumber || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">VAT-nummer</span>
                      <span className="info-value">{customer.vatnumber || '-'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-item">
                      <span className="info-label">Förnamn</span>
                      <span className="info-value">{customer.first_name || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Efternamn</span>
                      <span className="info-value">{customer.last_name || '-'}</span>
                    </div>
                  </>
                )}

                <div className="info-item">
                  <span className="info-label">Telefonnummer</span>
                  <span className="info-value">
                    {customer.phone ? (
                      <a href={`tel:${customer.phone}`} className="text-decoration-none">
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        {customer.phone}
                      </a>
                    ) : '-'}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Betalningsvillkor</span>
                  <span className="info-value">
                    {customer.payment_terms ? `${customer.payment_terms} dagar` : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  Adresser
                </h3>
              </div>

              <div className="address-grid">
                {customer.addresses && customer.addresses.length > 0 && (
                  customer.addresses.map((address, index) => (
                    <div key={index} className="address-card">
                      <div className="address-type">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        {address.address_type === 'billing' ? 'Fakturaadress' :
                          address.address_type === 'shipping' ? 'Leveransadress' : 'Annan adress'}
                      </div>
                      <div className="address-details">
                        <div className="detail-line">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon" />
                          {address.street1}{address.street2 ? `, ${address.street2}` : ''}
                        </div>
                        <div className="detail-line">
                          <span className="detail-icon"></span>
                          {address.postalcode} {address.city}
                        </div>
                        {address.country && (
                          <div className="detail-line">
                            <span className="detail-icon"></span>
                            {address.country}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}

                {/* Add Address Card */}
                <div
                  className="add-item-card"
                  onClick={() => setShowAddressModal(true)}
                >
                  <div className="add-item-icon">
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                  <p className="add-item-text">Lägg till adress</p>
                  <p className="add-item-subtitle">Fakturaadress, leveransadress eller annan</p>
                </div>
              </div>

              {customer.addresses && customer.addresses.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <p className="mb-3">Inga adresser har lagts till ännu</p>
                </div>
              )}
            </div>

            {/* Contacts (only for companies) */}
            {isCompany && (
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">
                    <FontAwesomeIcon icon={faUser} />
                    Kontaktpersoner
                  </h3>
                </div>

                <div className="contact-grid">
                  {customer.contacts && customer.contacts.length > 0 && (
                    customer.contacts.map((contact, index) => (
                      <div key={index} className="contact-card">
                        <div className="contact-role">
                          <FontAwesomeIcon icon={faUser} />
                          {contact.role || 'Kontaktperson'}
                        </div>
                        <div className="contact-details">
                          <div className="detail-line">
                            <FontAwesomeIcon icon={faUser} className="detail-icon" />
                            <strong>{contact.contact_name || 'Namn saknas'}</strong>
                          </div>
                          {contact.contact_email && (
                            <div className="detail-line">
                              <FontAwesomeIcon icon={faEnvelope} className="detail-icon" />
                              <a href={`mailto:${contact.contact_email}`} className="text-decoration-none">
                                {contact.contact_email}
                              </a>
                            </div>
                          )}
                          {contact.contact_phone && (
                            <div className="detail-line">
                              <FontAwesomeIcon icon={faPhone} className="detail-icon" />
                              <a href={`tel:${contact.contact_phone}`} className="text-decoration-none">
                                {contact.contact_phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Add Contact Card */}
                  <div
                    className="add-item-card"
                    onClick={() => setShowContactModal(true)}
                  >
                    <div className="add-item-icon">
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                    <p className="add-item-text">Lägg till kontaktperson</p>
                    <p className="add-item-subtitle">Ny kontakt för detta företag</p>
                  </div>
                </div>

                {customer.contacts && customer.contacts.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <p className="mb-3">Inga kontaktpersoner har lagts till ännu</p>
                  </div>
                )}
              </div>
            )}
          </Col>

          <Col lg={4}>
            {/* Quick Actions */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">Snabbåtgärder</h3>
              </div>
              <div className="d-grid gap-2">
                <Button variant="primary" className="btn-modern">
                  <FontAwesomeIcon icon={faFileInvoice} />
                  Skapa ny faktura
                </Button>
                <Button variant="outline-secondary" className="btn-modern">
                  <FontAwesomeIcon icon={faEdit} />
                  Redigera kunduppgifter
                </Button>
                <Button
                  variant="outline-secondary"
                  className="btn-modern"
                  onClick={() => setShowContactModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Lägg till kontakt
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Invoices Section */}
        <div className="info-card">
          <div className="info-card-header">
            <h3 className="info-card-title">
              <FontAwesomeIcon icon={faFileInvoice} />
              Fakturor ({invoices.length})
            </h3>
          </div>
          <InvoicesTable invoices={invoices} />
        </div>
      </div>

      {/* Add Address Modal */}
      <>
        <style>{`
          .address-modal-content {
            border-radius: 8px;
            border: none;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          
          .address-modal-header {
            background: white;
            color: #1e293b;
            padding: 1.5rem 2rem;
            border-bottom: 1px solid #e2e8f0;
            position: relative;
          }
          
          .address-modal-body {
            padding: 2rem;
            background: #ffffff;
            max-height: 70vh;
            overflow-y: auto;
          }
          
          .address-section-card {
            background: transparent;
            border: none;
            padding: 0;
            margin-bottom: 2rem;
          }
          
          .address-section-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
            padding: 0;
            border: none;
            background: transparent;
          }
          
          .address-section-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #1e293b;
            margin: 0;
          }
          
          .address-section-subtitle {
            font-size: 0.875rem;
            color: #64748b;
            margin: 0;
          }
          
          .address-type-selector {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1rem;
            margin-bottom: 2rem;
          }
          
          .address-type-option {
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            padding: 1.5rem;
            text-align: center;
            cursor: pointer;
            transition: all 0.2s ease;
            background: white;
          }
          
          .address-type-option:hover {
            border-color: #cbd5e1;
            background: #f8fafc;
          }
          
          .address-type-option.selected {
            border-color: var(--bs-primary);
            background: #eff7f1ff;
          }
          
          .address-type-icon {
            width: 48px;
            height: 48px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 0.75rem;
            background: #f1f5f9;
            color: #64748b;
            transition: all 0.2s ease;
          }
          
          .address-type-option.selected .address-type-icon {
            background: var(--bs-primary);
            color: white;
          }
          
          .address-type-label {
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 0.25rem;
          }
          
          .address-type-description {
            font-size: 0.875rem;
            color: #64748b;
          }
          
          .address-form-group {
            margin-bottom: 1.5rem;
          }
          
          .address-form-label {
            display: block;
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            margin-bottom: 0.5rem;
          }
          
          .address-form-label.required::after {
            content: " *";
            color: #ef4444;
          }
          
          .address-form-input {
            width: 100%;
            padding: 0.75rem 1rem;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: all 0.2s ease;
          }
          
          .address-form-input:focus {
            outline: none;
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 2px rgba(22, 107, 72, 0.1);
          }
          
          .address-form-grid {
            display: grid;
            gap: 1rem;
          }
          
          .address-grid-2 {
            grid-template-columns: 1fr 1fr;
          }
          
          .address-grid-3 {
            grid-template-columns: 1fr 1fr 1fr;
          }
          
          .address-modal-actions {
            background: #f8fafc;
            padding: 1.5rem 2rem;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .address-action-button {
            border-radius: 6px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            font-size: 0.875rem;
            border: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .address-btn-cancel {
            background: #f1f5f9;
            color: #64748b;
            border: 1px solid #e2e8f0;
          }
          
          .address-btn-cancel:hover {
            background: #e2e8f0;
            color: #475569;
          }
          
          .address-btn-save {
            background: var(--bs-primary);
            color: white;
          }
          
          .address-btn-save:hover {
            background: var(--bs-primary);
          }
          
          .address-close-button {
            position: absolute;
            top: 1rem;
            right: 1.5rem;
            background: #f1f5f9;
            border: 1px solid #e2e8f0;
            color: #64748b;
            width: 32px;
            height: 32px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }
          
          .address-close-button:hover {
            background: #e2e8f0;
            color: #475569;
          }
          
          .address-loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(59, 130, 246, 0.3);
            border-top: 2px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .address-type-selector {
              grid-template-columns: 1fr;
            }
            
            .address-grid-2, .address-grid-3 {
              grid-template-columns: 1fr;
            }
          }
        `}</style>

        <Modal
          className='fade'
          size='lg'
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          backdrop="static"
          centered
        >
          <div className="address-modal-content">
            <Form onSubmit={handleAddAddress}>
              {/* Header */}
              <div className="address-modal-header">
                <button type="button" className="address-close-button" onClick={() => setShowAddressModal(false)}>
                  <X size={16} />
                </button>
                <div className="d-flex align-items-center gap-3">
                  <MapPin size={24} />
                  <div>
                    <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      Lägg till adress
                    </h3>
                    <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      Skapa en ny adress för kunden
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="address-modal-body">
                {/* Address Type Selection */}
                <div className="address-section-card">
                  <div className="address-section-header">
                    <Building size={20} />
                    <div>
                      <h4 className="address-section-title">Adresstyp</h4>
                      <p className="address-section-subtitle">Välj typ av adress som ska läggas till</p>
                    </div>
                  </div>

                  <div className="address-type-selector">
                    <div
                      className={`address-type-option ${addressForm.address_type === 'billing' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'billing' })}
                    >
                      <div className="address-type-icon">
                        <FontAwesomeIcon icon={faFileInvoice} size="lg" />
                      </div>
                      <div className="address-type-label">Fakturaadress</div>
                      <div className="address-type-description">För fakturering och korrespondens</div>
                    </div>

                    <div
                      className={`address-type-option ${addressForm.address_type === 'shipping' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'shipping' })}
                    >
                      <div className="address-type-icon">
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" />
                      </div>
                      <div className="address-type-label">Leveransadress</div>
                      <div className="address-type-description">För leveranser och frakt</div>
                    </div>

                    <div
                      className={`address-type-option ${addressForm.address_type === 'other' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'other' })}
                    >
                      <div className="address-type-icon">
                        <FontAwesomeIcon icon={faBuilding} size="lg" />
                      </div>
                      <div className="address-type-label">Annan adress</div>
                      <div className="address-type-description">Övrig adressinformation</div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="address-section-card">
                  <div className="address-section-header">
                    <MapPin size={20} />
                    <div>
                      <h4 className="address-section-title">Adressinformation</h4>
                      <p className="address-section-subtitle">Fyll i adressdetaljerna</p>
                    </div>
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label required">Gatuadress</label>
                    <input
                      type="text"
                      className="address-form-input"
                      value={addressForm.street1}
                      onChange={(e) => setAddressForm({ ...addressForm, street1: e.target.value })}
                      placeholder="Gatunamn och nummer"
                      required
                    />
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label">Gatuadress 2 (valfritt)</label>
                    <input
                      type="text"
                      className="address-form-input"
                      value={addressForm.street2}
                      onChange={(e) => setAddressForm({ ...addressForm, street2: e.target.value })}
                      placeholder="Lägenhetsnummer, våning, etc."
                    />
                  </div>

                  <div className="address-form-grid address-grid-3">
                    <div className="address-form-group">
                      <label className="address-form-label required">Ort</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.city}
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                        placeholder="Stockholm"
                        required
                      />
                    </div>

                    <div className="address-form-group">
                      <label className="address-form-label required">Postnummer</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.postalcode}
                        onChange={(e) => setAddressForm({ ...addressForm, postalcode: e.target.value })}
                        placeholder="XXX XX"
                        required
                      />
                    </div>

                    <div className="address-form-group">
                      <label className="address-form-label">Land</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.country}
                        onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                        placeholder="Sverige"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="address-modal-actions">
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  Alla obligatoriska fält måste fyllas i
                </div>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="address-action-button address-btn-cancel"
                    onClick={() => setShowAddressModal(false)}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="address-action-button address-btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="address-loading-spinner"></div>
                        Sparar...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Lägg till adress
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          </div>
        </Modal>
      </>

      {/* Add Contact Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Lägg till kontaktperson
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddContact}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Namn</Form.Label>
              <Form.Control
                type="text"
                value={contactForm.contact_name}
                onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
                placeholder="För- och efternamn"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Roll/Titel</Form.Label>
              <Form.Control
                type="text"
                value={contactForm.role}
                onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                placeholder="t.ex. VD, Ekonomichef, Inköpsansvarig"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>E-postadress</Form.Label>
                  <Form.Control
                    type="email"
                    value={contactForm.contact_email}
                    onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                    placeholder="namn@företag.se"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Telefonnummer</Form.Label>
                  <Form.Control
                    type="tel"
                    value={contactForm.contact_phone}
                    onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                    placeholder="08-123 456 78"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowContactModal(false)}>
              Avbryt
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sparar...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Lägg till kontakt
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Redigera kund
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            {editForm.type === 'company' ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Företagsnamn</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Organisationsnummer</Form.Label>
                      <Form.Control
                        type="text"
                        value={editForm.orgnumber}
                        onChange={(e) => setEditForm({ ...editForm, orgnumber: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>VAT-nummer</Form.Label>
                      <Form.Control
                        type="text"
                        value={editForm.vatnumber}
                        onChange={(e) => setEditForm({ ...editForm, vatnumber: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Förnamn</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Efternamn</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>E-post</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Betalningsvillkor (dagar)</Form.Label>
              <Form.Control
                type="number"
                value={editForm.payment_terms}
                onChange={(e) => setEditForm({ ...editForm, payment_terms: parseInt(e.target.value) })}
                min="1"
                max="365"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Avbryt
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sparar...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Spara ändringar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ViewCustomerDetailed;
